'use strict';
import sxQuery from '../../sxQuery/sxQuery';
import Helper from '../utils/Helper';
import Icons from './Icons';
import SxQueryObject from '../../sxQuery/SxQueryObject';
import SxQueryUtils from '../../sxQuery/SxQueryUtils';
import DatePrecision from '../enums/DatePrecision';
import StringHelper from '../../global/StringHelper';
import UiColors from '../styles/UiColors';
const HISTOGRAM_BUCKET_COUNT = 50;
const getDateString = (val, factor = 1000) => {
    const dt = new Date(val * factor);
    let date = dt.getDate().toString();
    let month = (dt.getMonth() + 1).toString();
    if (date.length === 1) {
        date = `0${date}`;
    }
    if (month.length === 1) {
        month = `0${month}`;
    }
    return `${dt.getFullYear()}-${month}-${date}`;
};
var ValueType;
(function (ValueType) {
    ValueType["L"] = "l";
    ValueType["R"] = "r";
})(ValueType || (ValueType = {}));
var BarType;
(function (BarType) {
    BarType["Active"] = "active";
    BarType["Empty"] = "empty";
})(BarType || (BarType = {}));
const createHandle = (controls, type, min, max, val, unit, controlDescriptionId) => {
    const $el = sxQuery(`<div class="ss360-slider__handle ss360-slider__handle--${type}" tabindex="0" unselectable="on" draggable="false" ondragstart="return false" 
	role="slider" aria-controls="${controls}" aria-valuemin="${min}" aria-valuemax="${max}" aria-valuenow="${val}" aria-orientation="horizontal"></div>`);
    if (unit) {
        $el.attr('aria-valuetext', `${val} ${unit}`);
    }
    if (controlDescriptionId !== undefined) {
        $el.attr('aria-describedby', controlDescriptionId);
    }
    return $el;
};
const createBar = (type) => sxQuery(`<div role="presentation" class="ss360-slider__bar--${type} ss360-slider__bar" 
unselectable="on" draggable="false" ondragstart="return false"></div>`);
const createUnit = (label, forId, ariaLabel, filterName, unit) => {
    let unitDisplayed = '';
    if (unit !== undefined) {
        unitDisplayed = unit;
    }
    const $el = sxQuery(`<label id="${forId}-label" for="${forId}" class="ss360-slider__unit">${label.replace(/#UNIT#/g, unitDisplayed).trim()}</label>`);
    if (ariaLabel !== undefined && unit !== undefined) {
        $el.attr('aria-label', ariaLabel.replace(/#FILTER_NAME#/g, filterName).replace(/#UNIT#/g, unit).trim());
    }
    return $el;
};
const createResultCount = (text) => sxQuery(`<span class="ss360-slider__result-count">${StringHelper.escapeHtml(text)}</span>`);
const createSubmitButton = (label, text) => {
    const inner = text !== undefined && text.length > 0 ? `<span class="ss360-slider__submit-text">${text}</span>` : `<i class="ss360-slider__submit-icon">${Icons.getSvgIcon(Icons.SIMPLE_ARROW, UiColors.WHITE, 'ss360-slider__submit-svg')}</i>`;
    return sxQuery(`<button type="button" class="ss360-n-button ss360-slider__submit" title="${label}">${inner}</button>`);
};
const createValuePin = () => sxQuery(`<span class="ss360-slider__selected-value" role="presentation"></span>`);
const getMousePosition = (event) => ({
    x: event.clientX,
    y: event.clientY
});
const getTouchPosition = (event) => ({
    x: event.touches[0].clientX,
    y: event.touches[0].clientY
});
const isValueDistributionSkewed = (valueDistribution, min, max) => {
    const range = max - min;
    const interval = range / 5;
    const thresholds = [min + interval, min + 2 * interval, min + 3 * interval, min + 4 * interval];
    const counts = [0, 0, 0, 0, 0];
    const totalValues = Object.values(valueDistribution).reduce((sum, count) => sum + count, 0);
    const entries = Object.entries(valueDistribution);
    for (let i = 0; i < entries.length; i++) {
        const [value, count] = entries[i];
        const numericValue = parseFloat(value);
        if (numericValue < thresholds[0]) {
            counts[0] += count;
        }
        else if (numericValue < thresholds[1]) {
            counts[1] += count;
        }
        else if (numericValue < thresholds[2]) {
            counts[2] += count;
        }
        else if (numericValue < thresholds[3]) {
            counts[3] += count;
        }
        else {
            counts[4] += count;
        }
    }
    const validRanges = counts.filter(count => count / totalValues >= 0.15 || count / totalValues > 0.6).length;
    return validRanges < 2;
};
class RangeSlider {
    constructor(wrapper, config, valueDistribution, changeCallback, context, groupWrapper, maxResults) {
        this.eventNamePostfix = context.generateId('ss360Slider');
        this.wrapper = wrapper;
        this.groupWrapper = groupWrapper;
        this.config = Object.assign({}, config);
        this.initialStepSize = config.step;
        this.valueDistribution = valueDistribution;
        this.changeCallback = changeCallback;
        this.pluginConfiguration = context.pluginConfiguration;
        this.isMounted = false;
        this.isUpdatingWidth = false;
        this.maxResults = maxResults;
        if (config.datePrecision !== DatePrecision.MS) {
            this.dateConversionFactor = 1000;
        }
        else {
            this.dateConversionFactor = 1;
        }
        this.initSettings();
        this.lVal = this.config.min;
        this.rVal = this.config.max;
        this.lId = `ss360-slider--${Math.round(10000 * Math.random())}`;
        this.rId = `ss360-slider--${Math.round(10000 * Math.random())}`;
        this.createUiElements();
    }
    initSettings() {
        const config = this.config;
        if (config.min === undefined) {
            config.min = 1;
        }
        if (config.max === undefined) {
            config.max = 100;
        }
        // @ts-ignore
        if (config.step !== undefined && config.step !== 0 && config.step !== '0') {
            if (Math.round(config.step) !== config.step) {
                config.step = Math.round(config.step * 100) / 100;
            }
        }
        else {
            config.step = Math.abs(config.max - config.min) / 100;
        }
        if (config.drawHistogram === undefined && this.valueDistribution !== undefined && Object.keys(this.valueDistribution).length > 0) {
            config.drawHistogram = !isValueDistributionSkewed(this.valueDistribution, config.min, config.max);
        }
        config.drawHistogram = config.drawHistogram && this.valueDistribution !== undefined && Object.keys(this.valueDistribution).length > 0;
        if (config.drawHistogram) {
            this.groupWrapper.removeClass('ss360-range-filter--no-histogram');
            this.wrapper.removeClass('ss360-slider--no-histogram');
        }
        else {
            this.groupWrapper.addClass('ss360-range-filter--no-histogram');
            this.wrapper.addClass('ss360-slider--no-histogram');
        }
        if (this.config.isDate) {
            this.groupWrapper.addClass('ss360-range-filter--date');
        }
        else {
            this.groupWrapper.removeClass('ss360-range-filter--date');
        }
        if (config.showSlider === undefined) {
            config.showSlider = true;
        }
        if (config.fromLabel === undefined) {
            config.fromLabel = this.pluginConfiguration.filters.dateFrom;
        }
        if (config.toLabel === undefined) {
            config.toLabel = this.pluginConfiguration.filters.dateTo;
        }
    }
    createUiElements() {
        const { isDate, unit, min, max, filterName } = this.config;
        const { sliderMinUnitLabel, sliderMaxUnitLabel, sliderMinUnitAriaLabel, sliderMaxUnitAriaLabel, submitButtonLabel, submitButtonText, applyFilterAriaLabel } = this.pluginConfiguration.filters;
        const controlDescriptionId = `ss360-slider-control-${Math.round(Math.random() * 100000)}`;
        this.lHandle = createHandle(this.lId, ValueType.L, min, max, min, unit, controlDescriptionId);
        this.rHandle = createHandle(this.rId, ValueType.R, min, max, max, unit, controlDescriptionId);
        this.lHandle.attr('aria-labelledby', `${this.lId}-label`);
        this.rHandle.attr('aria-labelledby', `${this.rId}-label`);
        const { sliderResultCountLabel } = this.pluginConfiguration.filters;
        let totalResults = Object.keys(this.valueDistribution).reduce((acc, key) => acc + this.valueDistribution[key], 0);
        if (this.maxResults !== undefined) {
            totalResults = Math.min(totalResults, this.maxResults);
        }
        const totalResultsFormatted = 'Intl' in window ? new Intl.NumberFormat().format(totalResults) : totalResults.toString();
        if (sliderResultCountLabel) {
            this.resultCountLabel = createResultCount(sliderResultCountLabel.replace(/#COUNT#/g, totalResultsFormatted));
        }
        else {
            this.resultCountLabel = new SxQueryObject([]);
        }
        this.emptyBar = createBar(BarType.Empty);
        this.activeBar = createBar(BarType.Active);
        this.lInput = this.createInput(this.lId, min, ValueType.L);
        this.rInput = this.createInput(this.rId, max, ValueType.R);
        this.lUnit = createUnit(isDate ? this.config.fromLabel : sliderMinUnitLabel, this.lId, isDate ? undefined : sliderMinUnitAriaLabel, filterName, unit);
        this.rUnit = createUnit(isDate ? this.config.toLabel : sliderMaxUnitLabel, this.rId, isDate ? undefined : sliderMaxUnitAriaLabel, filterName, unit);
        this.submitButton = createSubmitButton(submitButtonLabel, submitButtonText);
        this.submitButton.attr('aria-label', applyFilterAriaLabel.replace(/#FILTER_NAME#/g, filterName));
        this.submitButton.on('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.notifyChange(true);
        });
        this.selectedValuePin = createValuePin();
        this.controlDescription = sxQuery(`<span id="${controlDescriptionId}" class="ss360-slider__control-description ss360-sr-only">${this.pluginConfiguration.filters.sliderControlDescription}</span>`);
    }
    createInput(id, val, type) {
        const { isDate, min, max, step } = this.config;
        const ariaLabel = `${type === 'l' ? 'Min' : 'Max'} ${this.config.unit || 'value'}`;
        if (isDate) {
            const wrap = sxQuery('<div class="ss360-slider__date-wrap"></div>');
            const minDateString = getDateString(min, this.dateConversionFactor);
            const maxDateString = getDateString(max, this.dateConversionFactor);
            const currentDate = getDateString(val, this.dateConversionFactor);
            wrap.append(`<input class="ss360-n-input ss360-slider__input ss360-slider__input--date" 
				aria-label="${ariaLabel}" type="date" min="${minDateString}" max="${maxDateString}" value="${currentDate}" id="${id}" autocomplete="off">`);
            return wrap;
        }
        let inputMax = max;
        const inputStep = Math.round(step * 100) / 100;
        const rest = (max + min) % inputStep;
        inputMax += rest; // allow number input up to the max
        return sxQuery(`<div class="ss360-slider__value-wrap"><input type="number" placeholder="${val}"
		class="ss360-slider__input ss360-n-input" min="${min}" max="${inputMax}" step="${inputStep}" value="${val}" id="${id}" autocomplete="off"></div>`);
    }
    mount() {
        const wrapper = this.wrapper;
        const { showSlider, isDate, prefix, postfix, drawHistogram, min, max } = this.config;
        const { lInput, rInput, emptyBar, activeBar, lHandle, rHandle, submitButton, lUnit, rUnit, selectedValuePin, resultCountLabel } = this;
        if (!this.isMounted) {
            wrapper.html('');
            wrapper.prepend(resultCountLabel);
            wrapper.append(this.controlDescription);
            if (showSlider && !isDate) {
                wrapper.append(selectedValuePin);
            }
            if (showSlider) {
                const sliderWrap = sxQuery('<div class="ss360-slider__wrap"></div>');
                sliderWrap.append(emptyBar).append(activeBar).append(lHandle).append(rHandle);
                wrapper.append(sliderWrap);
            }
            const inputWrap = sxQuery('<div class="ss360-slider__input-wrap"></div>');
            if (isDate) {
                const dateInputWrap = sxQuery('<div class="ss360-flex ss360-flex--column">');
                dateInputWrap.append(lInput).append(rInput);
                inputWrap.append(dateInputWrap);
            }
            else {
                inputWrap.append(lInput).append(rInput);
            }
            inputWrap.append(submitButton);
            if (isDate) {
                inputWrap.addClass('ss360-slider__input-wrap--date');
            }
            if (prefix !== undefined) {
                lInput.prepend(`<span class="ss360-slider__input--prefix">${prefix}</span>`);
            }
            if (postfix !== undefined) {
                rInput.prepend(`<span class="ss360-slider__input--postfix">${postfix}</span>`);
            }
            wrapper.append(inputWrap);
            if (drawHistogram) {
                wrapper.parent().addClass('ss360-filter__content--histogram');
            }
        }
        lInput.prepend(lUnit);
        rInput.prepend(rUnit);
        if (drawHistogram && !this.isMounted && min !== max) {
            this.histogram = this.createHistogram();
            wrapper.find('.ss360-slider__wrap').prepend(this.histogram);
            this.lHistBox = this.histogram.find('.ss360-slider__histogram-min');
            this.rHistBox = this.histogram.find('.ss360-slider__histogram-max');
        }
        this.updateFullWidth(false, this.activeHandle);
        setTimeout(() => {
            this.updateFullWidth(false, this.activeHandle);
        }, 200);
        if (!this.isMounted) {
            this.updateDisabledState(min, max);
            sxQuery(window).on(`resize.${this.eventNamePostfix}`, this.mount.bind(this));
        }
        this.isMounted = true;
    }
    updateDisabledState(min, max) {
        const { lInput, rInput, activeBar, lHandle, rHandle, submitButton } = this;
        const isDisabled = min === max;
        const inactiveHandleClass = 'ss360-slider__handle--disabled';
        const inactiveBarClass = 'ss360-slider__bar--disabled';
        const inactiveSubmitButtonClass = 'ss360-slider__submit--disabled';
        if (!isDisabled) {
            this.bindEventStart(lHandle, ValueType.L);
            this.bindEventStart(rHandle, ValueType.R);
            this.bindArrowKeys(lHandle, ValueType.L);
            this.bindArrowKeys(rHandle, ValueType.R);
            this.bindBarClicks();
            this.bindInputChanges();
            lHandle.removeClass(inactiveHandleClass);
            rHandle.removeClass(inactiveHandleClass);
            activeBar.removeClass(inactiveBarClass);
            submitButton.removeClass(inactiveSubmitButtonClass);
        }
        else {
            lHandle.addClass(inactiveHandleClass);
            rHandle.addClass(inactiveHandleClass);
            activeBar.addClass(inactiveBarClass);
            submitButton.addClass(inactiveSubmitButtonClass);
            this.clearEventListeners();
            lHandle.css('left', null);
            rHandle.css('left', null);
            this.activeBar.css('width', null).css('left', null);
        }
        lInput.find('input').attr('disabled', isDisabled ? 'disabled' : null);
        rInput.find('input').attr('disabled', isDisabled ? 'disabled' : null);
        submitButton.attr('disabled', isDisabled ? 'disabled' : null);
    }
    clearEventListeners() {
        const { lHandle, rHandle } = this;
        const handles = [lHandle, rHandle];
        handles.forEach((handle) => {
            handle.off(`mousedown.${this.eventNamePostfix}, touchstart.${this.eventNamePostfix}, focus.${this.eventNamePostfix}, focusout.${this.eventNamePostfix}, keydown.${this.eventNamePostfix}, blur.${this.eventNamePostfix}`);
        });
        this.activeBar.off(`click.${this.eventNamePostfix}`);
        this.emptyBar.off(`click.${this.eventNamePostfix}`);
        this.lInput.find('input').off(`input.${this.eventNamePostfix},keyup.${this.eventNamePostfix},focusout.${this.eventNamePostfix}`);
        this.rInput.find('input').off(`input.${this.eventNamePostfix},keyup.${this.eventNamePostfix},focusout.${this.eventNamePostfix}`);
    }
    createHistogram() {
        const { min, max } = this.config;
        const buckets = new Array(HISTOGRAM_BUCKET_COUNT);
        const bucketStep = (max - min) / (HISTOGRAM_BUCKET_COUNT - 1);
        Object.keys(this.valueDistribution).forEach((key) => {
            // @ts-ignore
            const count = parseInt(this.valueDistribution[key], 10);
            const dVal = parseFloat(key);
            const bucketIdx = Math.floor((dVal - min) / bucketStep);
            if (buckets[bucketIdx] === undefined) {
                buckets[bucketIdx] = 0;
            }
            buckets[bucketIdx] += count;
        });
        const maxBucketSize = buckets.reduce((acc, val) => Math.max(acc, val), 0);
        const svgLines = buckets.reduce((acc, val, idx) => {
            if (val !== undefined) {
                const x = idx * 2 + 1;
                const y1 = 100 - Math.round((val / maxBucketSize) * 100);
                const y2 = 100;
                const width = 1.5;
                const height = Math.abs(y2 - y1);
                acc.push(`<rect x="${x - width / 2}%" y="${y1}%" width="${width}%" height="${height}%" fill="${UiColors.ACCENT_LIGHT}" rx="2" ry="2"/>`);
            }
            return acc;
        }, []);
        const histogram = sxQuery(`<svg role="presentation" class="ss360-slider__histogram" height="70" fill="none" xmlns="http://www.w3.org/2000/svg">
					${svgLines.join('')}
					<rect class="ss360-slider__histogram-min" x="0%" y="0%" fill="${UiColors.WHITE}" stroke="${UiColors.WHITE}" width="0%" height="100%" />
					<rect class="ss360-slider__histogram-max" x="100%" y="0%" fill="${UiColors.WHITE}" stroke="${UiColors.WHITE}" width="0%" height="100%" />
				</svg>`);
        return histogram;
    }
    setCurrentMin(minVal, update = true) {
        if (this.lVal < this.rVal) {
            this.lVal = minVal;
            this.lVal = this.clamp(this.lVal);
        }
        else {
            this.rVal = minVal;
            this.rVal = this.clamp(this.rVal);
        }
        if (update) {
            this.updateAll();
        }
    }
    setCurrentMax(maxVal, update = true) {
        if (this.lVal > this.rVal) {
            this.lVal = maxVal;
            this.lVal = this.clamp(this.lVal);
        }
        else {
            this.rVal = maxVal;
            this.rVal = this.clamp(this.rVal);
        }
        if (update) {
            this.updateAll();
        }
    }
    clamp(value) {
        const { min, max } = this.config;
        return Math.min(max, Math.max(min, value));
    }
    updateFullWidth(sync = false, activeHandle = undefined) {
        const handler = () => {
            const fWidthCache = this.fullWidth;
            const tempEmptyBar = this.wrapper.find('.ss360-slider__bar--empty');
            // @ts-ignore
            this.fullWidth = tempEmptyBar.outerWidth() - parseInt(tempEmptyBar.css('padding-left') || '0', 10) - parseInt(tempEmptyBar.css('padding-right') || '0', 10);
            if (fWidthCache !== this.fullWidth) {
                const { max, min, step } = this.config;
                const range = max - min;
                const stepCount = (range) / step;
                this.dStep = this.fullWidth / stepCount;
                this.updateAll();
                this.activeHandle = ValueType.R;
                this.updateHandlePosition();
                this.activeHandle = ValueType.L;
                this.updateHandlePosition();
                this.activeHandle = activeHandle;
            }
            if (!sync) {
                this.isUpdatingWidth = false;
            }
            this.lHandle.addClass('ss360-slider__handle--mounted');
            this.rHandle.addClass('ss360-slider__handle--mounted');
        };
        if (sync) {
            handler();
            return;
        }
        if (this.isUpdatingWidth)
            return;
        this.isUpdatingWidth = true;
        SxQueryUtils.requestAnimationFrame(handler);
    }
    updateAll() {
        this.updateHandlePosition();
        this.updateActiveBar();
        this.updateInputs();
        this.updateHistogram();
        this.updateResultCount();
        this.updateValuePinPosition();
    }
    notifyChangeThrotteled() {
        clearTimeout(this.notificationTimeout);
        // @ts-ignore
        this.notificationTimeout = setTimeout(() => {
            this.notifyChange();
        }, 600);
    }
    notifyChange(isButton = false) {
        clearTimeout(this.notificationTimeout);
        this.notificationTimeout = -1;
        if (this.changeCallback !== undefined && typeof this.changeCallback === 'function') {
            this.changeCallback.call(this.wrapper, Math.min(this.lVal, this.rVal), Math.max(this.lVal, this.rVal), isButton);
        }
    }
    updateValue(deltaX) {
        let diff = (deltaX / this.dStep) * this.config.step;
        diff -= diff % this.config.step;
        const updatedValue = this.clamp(this.valCache + diff);
        if (this.activeHandle === ValueType.L) {
            this.lVal = updatedValue;
        }
        else if (this.activeHandle === ValueType.R) {
            this.rVal = updatedValue;
        }
        this.updateAll();
    }
    updateHandlePosition() {
        const activeHandle = this.activeHandle;
        let aHandle;
        let aVal;
        if (activeHandle === ValueType.L) {
            aHandle = this.lHandle;
            aVal = this.lVal;
        }
        else if (activeHandle === ValueType.R) {
            aHandle = this.rHandle;
            aVal = this.rVal;
        }
        if (aHandle === undefined || aVal === undefined) {
            return;
        }
        const newPosition = this.getValuePositionInPx(aVal);
        aHandle.css('left', newPosition);
        aHandle.attr('aria-valuenow', `${aVal}`);
        if (this.config.unit) {
            aHandle.attr('aria-valuetext', `${aVal} ${this.config.unit}`);
        }
        this.lHandle.attr('aria-controls', this.lVal < this.rVal ? this.lId : this.rId);
        this.rHandle.attr('aria-controls', this.lVal < this.rVal ? this.rId : this.lId);
    }
    updateValuePinPosition() {
        this.updateValuePin(this.activeHandle === ValueType.L ? this.lHandle : this.rHandle, this.activeHandle);
    }
    updateActiveBar() {
        const minVal = Math.min(this.lVal, this.rVal);
        const maxVal = Math.max(this.lVal, this.rVal);
        const minX = this.getValuePositionInPx(minVal);
        const maxX = this.getValuePositionInPx(maxVal);
        this.activeBar.css('width', Math.max(0, maxX - minX));
        this.activeBar.css('left', minX);
    }
    getValuePositionInPx(value) {
        const { min, step } = this.config;
        return ((value - min) / step) * this.dStep;
    }
    updateInputs() {
        const newMin = Math.min(this.lVal, this.rVal);
        const newMax = Math.max(this.lVal, this.rVal);
        const toFixedValue = (val) => {
            if (this.config.decimalPlaces !== undefined && Math.round(parseFloat(val)) !== parseFloat(val)) {
                return parseFloat(val).toFixed(this.config.decimalPlaces);
            }
            return val;
        };
        const isDate = this.config.isDate;
        const lRes = isDate ? getDateString(newMin, this.dateConversionFactor) : toFixedValue(Math.round(newMin * 100) / 100);
        const rRes = isDate ? getDateString(newMax, this.dateConversionFactor) : toFixedValue(Math.round(newMax * 100) / 100);
        this.lInput.find('input').val(lRes); // round to two decimals
        this.rInput.find('input').val(rRes);
    }
    updateHistogram() {
        if (this.lHistBox !== undefined && this.rHistBox !== undefined) {
            const { lVal, rVal } = this;
            const { min, max } = this.config;
            const newMin = Math.min(lVal, rVal);
            const newMax = Math.max(lVal, rVal);
            const range = max - min;
            const minDiff = newMin - min;
            const maxDiff = max - newMax;
            const lPositionNum = ((minDiff / range) * 100);
            const rPositionNum = ((maxDiff / range) * 100);
            const lPosition = lPositionNum.toFixed(2);
            const rPosition = rPositionNum.toFixed(2);
            const rOffsetX = 100 - parseFloat(rPosition);
            this.lHistBox.attr('width', Helper.isNaN(lPositionNum) ? null : `${lPosition}%`);
            this.rHistBox.attr('width', Helper.isNaN(rPositionNum) ? null : `${rPosition}%`);
            this.rHistBox.attr('x', Helper.isNaN(rOffsetX) ? null : `${rOffsetX}%`);
        }
    }
    updateResultCount() {
        const { sliderResultCountLabel } = this.pluginConfiguration.filters;
        if (sliderResultCountLabel) {
            let min = this.lVal;
            let max = this.rVal;
            if (min > max) {
                min = this.rVal;
                max = this.lVal;
            }
            // @ts-ignore
            let totalResults = Object.keys(this.valueDistribution).reduce((acc, key) => {
                if (key < min || key > max) {
                    return acc;
                }
                return acc + this.valueDistribution[key];
            }, 0);
            if (this.maxResults !== undefined) {
                totalResults = Math.min(totalResults, this.maxResults);
            }
            this.resultCountLabel.text(sliderResultCountLabel.replace(/#COUNT#/g, 'Intl' in window ? new Intl.NumberFormat().format(totalResults) : totalResults.toString()));
        }
    }
    bindEventStart(handle, which) {
        handle.on(`mousedown.${this.eventNamePostfix}`, (e) => {
            if (e.which !== 1) {
                return;
            }
            // e.preventDefault();
            e.stopPropagation();
            this.activeHandle = which;
            this.valCache = which === ValueType.L ? this.lVal : this.rVal;
            const mousePosition = getMousePosition(e);
            this.startX = mousePosition.x;
            this.bindEventHandler('mousemove');
            this.showValuePin(handle, which);
        });
        handle.on(`touchstart.${this.eventNamePostfix}`, undefined, (e) => {
            if (e.touches.length !== 1) {
                return;
            }
            this.activeHandle = which;
            this.valCache = which === ValueType.L ? this.lVal : this.rVal;
            const touchPosition = getTouchPosition(e);
            this.startX = touchPosition.x;
            this.bindEventHandler('touchmove');
            handle.addClass('ss360-slider__handle--focus');
            if (this.config.drawHistogram && this.histogram !== undefined) {
                this.histogram.addClass('ss360-slider__histogram--active');
            }
            this.showValuePin(handle, which);
        }, {
            passive: false
        });
        handle.on(`focus.${this.eventNamePostfix}`, () => {
            this.activeBar.addClass('ss360-slider__bar--no-transition');
        });
        handle.on(`focusout.${this.eventNamePostfix}`, () => {
            this.activeBar.removeClass('ss360-slider__bar--no-transition');
        });
    }
    bindArrowKeys(handle, which) {
        const downKeyCodes = [34, 37, 40];
        const upKeyCodes = [33, 38, 39];
        const downKeys = ['PageDown', 'ArrowLeft', 'ArrowDown'];
        const upKeys = ['PageUp', 'ArrowRight', 'ArrowUp'];
        const { step, min, max } = this.config;
        handle.on(`keydown.${this.eventNamePostfix}`, (e) => {
            let by;
            if (downKeyCodes.indexOf(e.which) !== -1 || downKeys.indexOf(e.key) !== -1 || downKeys.indexOf(e.code) !== -1) {
                by = step * -1;
            }
            else if (upKeyCodes.indexOf(e.which) !== -1 || upKeys.indexOf(e.key) !== -1 || upKeys.indexOf(e.code) !== -1) {
                by = step;
            }
            else if (e.which === 36 || e.key === 'Home' || e.code === 'Home') {
                by = (max - min) * -1;
            }
            else if (e.which === 35 || e.key === 'End' || e.code === 'End') {
                by = (max - min);
            }
            if (by !== undefined) {
                e.preventDefault();
                e.stopPropagation();
                if (which === ValueType.L) {
                    this.lVal = this.clamp(this.lVal + by);
                }
                else if (which === ValueType.R) {
                    this.rVal = this.clamp(this.rVal + by);
                }
                this.activeHandle = which;
                this.updateAll();
                this.notifyChangeThrotteled();
                this.activeHandle = undefined;
            }
        });
    }
    bindInputChanges() {
        let pendingLChange;
        let pendingRChange;
        const applyPendingChanges = (l = true, r = true) => {
            if (pendingLChange !== undefined && l) {
                clearTimeout(this.lChangeTimeoutId);
                this.inputChangeHandler(ValueType.L, {
                    target: {
                        value: pendingLChange
                    }
                });
                pendingLChange = undefined;
            }
            if (pendingRChange !== undefined && r) {
                clearTimeout(this.rChangeTimeoutId);
                this.inputChangeHandler(ValueType.R, {
                    target: {
                        value: pendingRChange
                    }
                });
                pendingRChange = undefined;
            }
        };
        const onKeyUp = (e) => {
            if (e.which === 13 || e.keyCode === 13 || e.code === 'Enter' || e.key === 'Enter') {
                applyPendingChanges();
                this.notifyChange();
            }
        };
        this.lInput.find('input').on(`input.${this.eventNamePostfix}`, (e) => {
            pendingLChange = e.target.value;
            clearTimeout(this.lChangeTimeoutId);
            const setChangeTimeout = () => {
                // @ts-ignore
                this.lChangeTimeoutId = setTimeout(() => {
                    if (SxQueryUtils.isElementActive(e.target)) {
                        setChangeTimeout();
                    }
                    else {
                        pendingLChange = undefined;
                        this.inputChangeHandler(ValueType.L, e);
                    }
                }, 300);
            };
            setChangeTimeout();
        }).on(`keyup.${this.eventNamePostfix}`, onKeyUp).on(`focusout.${this.eventNamePostfix}`, () => {
            applyPendingChanges(true, false);
        });
        this.rInput.find('input').on(`input.${this.eventNamePostfix}`, (e) => {
            pendingRChange = e.target.value;
            clearTimeout(this.rChangeTimeoutId);
            const setChangeTimeout = () => {
                // @ts-ignore
                this.rChangeTimeoutId = setTimeout(() => {
                    if (SxQueryUtils.isElementActive(e.target)) {
                        setChangeTimeout();
                    }
                    else {
                        pendingRChange = undefined;
                        this.inputChangeHandler(ValueType.R, e);
                    }
                }, 300);
            };
            setChangeTimeout();
        }).on(`keyup.${this.eventNamePostfix}`, onKeyUp).on(`focusout.${this.eventNamePostfix}`, () => {
            applyPendingChanges(false, true);
        });
        ;
    }
    inputChangeHandler(which, e) {
        let val = e.target.value;
        if (this.config.isDate) {
            const dt = new Date(e.target.value);
            try {
                const stringValParts = e.target.value.split('-');
                const year = stringValParts[0];
                const month = stringValParts[1] - 1;
                const day = stringValParts[2];
                const timestamp = Date.UTC(year, month, day, 0, 0, 0, 0);
                val = timestamp / this.dateConversionFactor;
            }
            catch (ex) {
                val = dt.getTime() / this.dateConversionFactor;
            }
        }
        if (this.config.isDate && Helper.isNaN(val)) { // e.g. cross
            return;
        }
        const newValue = this.clamp(val);
        if (which === ValueType.L) {
            if (this.lVal < this.rVal) {
                this.lVal = newValue;
                which = ValueType.L;
            }
            else {
                this.rVal = newValue;
                which = ValueType.R;
            }
        }
        else if (which === ValueType.R) {
            if (this.lVal > this.rVal) {
                this.lVal = newValue;
                which = ValueType.L;
            }
            else {
                this.rVal = newValue;
                which = ValueType.R;
            }
        }
        this.activeHandle = which;
        this.updateHandlePosition();
        this.updateActiveBar();
        this.updateHistogram();
        this.updateResultCount();
        this.updateValuePinPosition();
        this.activeHandle = undefined;
    }
    bindBarClicks() {
        this.lHandle.on(`blur.${this.eventNamePostfix}`, () => {
            this.lBlurTime = new Date().getTime();
        });
        this.rHandle.on(`blur.${this.eventNamePostfix}`, () => {
            this.rBlurTime = new Date().getTime();
        });
        this.activeBar.on(`click.${this.eventNamePostfix}`, (e) => {
            this.barClickHandler(e);
        });
        this.emptyBar.on(`click.${this.eventNamePostfix}`, (e) => {
            this.barClickHandler(e);
        });
    }
    barClickHandler(e) {
        const clickPosition = getMousePosition(e);
        const clickOfffset = clickPosition.x - this.emptyBar.offset().left;
        const lValueOffset = this.getValuePositionInPx(this.lVal);
        const rValueOffset = this.getValuePositionInPx(this.rVal);
        const distanceToL = Math.abs(clickOfffset - lValueOffset);
        const distanceToR = Math.abs(clickOfffset - rValueOffset);
        const curMs = new Date().getTime();
        const closerValue = distanceToL < distanceToR ? lValueOffset : rValueOffset;
        let deltaX = clickOfffset - closerValue;
        if (this.lBlurTime > (curMs - 300)) {
            this.activeHandle = ValueType.L;
            this.valCache = this.lVal;
            deltaX = ((clickOfffset < lValueOffset ? -1 : 1) * distanceToL);
        }
        else if (this.rBlurTime > (curMs - 300)) {
            this.activeHandle = ValueType.R;
            this.valCache = this.rVal;
            deltaX = ((clickOfffset < rValueOffset ? -1 : 1) * distanceToR);
        }
        else if (this.activeHandle === undefined || this.valCache === undefined) {
            this.activeHandle = distanceToL < distanceToR ? ValueType.L : ValueType.R;
            this.valCache = this.activeHandle === ValueType.L ? this.lVal : this.rVal;
        }
        this.updateValue(deltaX);
        this.notifyChange();
        this.activeHandle = undefined;
        this.valCache = undefined;
    }
    bindEventHandler(type) {
        if (type === 'mousemove') {
            sxQuery(window).on(`mousemove.${this.eventNamePostfix}`, (e) => {
                e.preventDefault();
                e.stopPropagation();
                const mousePosition = getMousePosition(e);
                this.updateValue(mousePosition.x - this.startX);
            });
            sxQuery(window).on(`mouseup.${this.eventNamePostfix}`, (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.activeHandle = undefined;
                this.valCache = undefined;
                sxQuery(window).off(`mousemove.${this.eventNamePostfix}, mouseup.${this.eventNamePostfix}`);
                this.notifyChange();
                this.hideValuePin();
            });
        }
        else if (type === 'touchmove') {
            sxQuery(window).on(`touchmove.${this.eventNamePostfix}`, (e) => {
                if (e.touches.length !== 1) {
                    return;
                }
                try {
                    e.preventDefault();
                }
                catch (ex) {
                    // ccl
                }
                e.stopPropagation();
                const touchPosition = getTouchPosition(e);
                this.updateValue(touchPosition.x - this.startX);
            });
            sxQuery(window).on(`touchend.${this.eventNamePostfix}`, (e) => {
                // e.preventDefault();
                e.stopPropagation();
                sxQuery(window).off(`touchend.${this.eventNamePostfix}, touchmove.${this.eventNamePostfix}`);
                this.lHandle.removeClass('ss360-slider__handle--focus');
                this.rHandle.removeClass('ss360-slider__handle--focus');
                if (this.config.drawHistogram && this.histogram !== undefined) {
                    this.histogram.removeClass('ss360-slider__histogram--active');
                }
                this.activeHandle = undefined;
                this.valCache = undefined;
                this.notifyChange();
                this.hideValuePin();
            });
        }
    }
    hideValuePin() {
        this.selectedValuePin.removeClass('ss360-slider__selected-value--active');
    }
    showValuePin(handle, which) {
        this.selectedValuePin.addClass('ss360-slider__selected-value--active');
        this.updateValuePin(handle, which);
    }
    updateValuePin(handle, which) {
        const val = which === ValueType.L ? this.lVal : this.rVal;
        const numFormatted = 'Intl' in window ? new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: this.config.decimalPlaces !== undefined ? this.config.decimalPlaces : 2
        }).format(val) : val.toString();
        const unit = this.config.unit ? `${this.config.unit}` : '';
        this.selectedValuePin.text(Helper.addUnitToNumber(numFormatted, unit));
        SxQueryUtils.requestAnimationFrame(() => {
            // @ts-ignore
            const left = parseInt(handle.css('left'), 10);
            const outerWidth = this.selectedValuePin.outerWidth();
            const handleWidth = handle.outerWidth();
            const leftPosition = Math.round(left - outerWidth / 2 + handleWidth / 2);
            this.selectedValuePin.css('left', `${leftPosition}px`);
        });
    }
    updateMinMax(min, max, currentMin, currentMax, counts) {
        if (currentMin === undefined) {
            currentMin = min;
        }
        if (currentMax === undefined) {
            currentMax = max;
        }
        this.config.min = min;
        this.config.max = max;
        if (this.activeHandle === ValueType.L || this.activeHandle === ValueType.R) {
            const isL = this.activeHandle === ValueType.L;
            const handle = isL ? this.lHandle : this.rHandle;
            // @ts-ignore
            const percentage = parseFloat(handle.css('left').replace('px', '')) / this.fullWidth;
            const diff = max - min;
            const val = diff * percentage;
            if (isL) {
                currentMin = val;
            }
            else {
                currentMax = val;
            }
        }
        // @ts-ignore
        if (this.initialStepSize === undefined || this.initialStepSize === 0 || this.initialStepSize === '0') {
            this.config.step = undefined;
        }
        this.setCurrentMin(currentMin, false);
        this.setCurrentMax(currentMax, false);
        this.initSettings();
        const updateHandleAttrs = (handle, val) => {
            if (handle === undefined) {
                return;
            }
            handle.attr('aria-valuemin', min.toString());
            handle.attr('aria-valuemax', max.toString());
            handle.attr('aria-valuenow', val.toString());
            if (this.config.unit) {
                handle.attr('aria-valuetext', `${val} ${this.config.unit}`);
            }
        };
        const updateInputAttrs = (input) => {
            if (input === undefined || input.length === 0) {
                return;
            }
            if (input.get(0).tagName !== 'input') {
                input = input.find('input');
            }
            if (this.config.isDate) {
                const minDateString = getDateString(min, this.dateConversionFactor);
                const maxDateString = getDateString(max, this.dateConversionFactor);
                input.attr('min', minDateString);
                input.attr('max', maxDateString);
                return;
            }
            let inputMax = max;
            const inputStep = Math.round(this.config.step * 100) / 100;
            const rest = (max + min) % inputStep;
            inputMax += rest; // allow number input up to the max
            input.attr('min', min.toString());
            input.attr('max', inputMax.toString());
            input.attr('step', inputStep.toString());
        };
        updateHandleAttrs(this.lHandle, currentMin);
        updateHandleAttrs(this.rHandle, currentMax);
        updateInputAttrs(this.lInput);
        updateInputAttrs(this.rInput);
        this.valueDistribution = counts || this.valueDistribution;
        if (this.config.drawHistogram && min !== max && this.histogram) {
            const histogram = this.createHistogram();
            this.histogram.get(0).outerHTML = histogram.get(0).outerHTML;
            this.histogram = this.wrapper.find('.ss360-slider__histogram');
            this.lHistBox = this.histogram.find('.ss360-slider__histogram-min');
            this.rHistBox = this.histogram.find('.ss360-slider__histogram-max');
            this.histogram.show();
        }
        else if (this.histogram) {
            this.histogram.hide();
        }
        this.updateDisabledState(min, max);
        this.fullWidth = undefined;
        this.updateFullWidth(true, this.activeHandle);
        this.updateAll();
        this.mount();
    }
}
export default RangeSlider;
